import React from "react";
import {
  Box,
  Checkbox,
  Grid,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MimeIcon from "./MimeIcon";
import { fileUrl } from "./app/api";
import { humanReadableSize } from "./app/utils";

export interface FileItem {
  key: string;
  size: number;
  uploaded: string;
  httpMetadata: { contentType: string };
  customMetadata?: { thumbnail?: string };
}

function extractFilename(key: string) {
  return key.split("/").pop();
}

export function isDirectory(file: FileItem) {
  return file.httpMetadata?.contentType === "application/x-directory";
}

function FileIcon({ file, puburl }: { file: FileItem; puburl?: string }) {
  return file.customMetadata?.thumbnail ? (
    <img
      src={fileUrl(
        `_$flaredrive$/thumbnails/${file.customMetadata.thumbnail}.png`,
        puburl
      )}
      alt={file.key}
      style={{ width: 36, height: 36, objectFit: "cover" }}
    />
  ) : (
    <MimeIcon contentType={file.httpMetadata.contentType} />
  );
}

function FileGrid({
  files,
  onCwdChange,
  multiSelected,
  onMultiSelect,
  emptyMessage,
  puburl,
}: {
  files: FileItem[];
  onCwdChange: (newCwd: string) => void;
  multiSelected: string[] | null;
  onMultiSelect: (key: string) => void;
  emptyMessage?: React.ReactNode;
  puburl?: string;
}) {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up("md"));

  const openFile = (file: FileItem) => {
    if (isDirectory(file)) {
      onCwdChange(file.key + "/");
      return;
    }

    window.open(fileUrl(file.key, puburl), "_blank", "noopener,noreferrer");
  };

  if (files.length === 0) return emptyMessage;

  if (desktop) {
    return (
      <TableContainer sx={{ flexGrow: 1, overflow: "auto" }}>
        <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Name</TableCell>
              <TableCell sx={{ width: 220 }}>Modified</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>
                Size
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => {
              const selected = Boolean(multiSelected?.includes(file.key));
              return (
                <TableRow
                  key={file.key}
                  hover
                  selected={selected}
                  onClick={() => {
                    if (multiSelected !== null) onMultiSelect(file.key);
                    else openFile(file);
                  }}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    onMultiSelect(file.key);
                  }}
                  sx={{ cursor: "pointer", userSelect: "none" }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selected}
                      onClick={(event) => {
                        event.stopPropagation();
                        onMultiSelect(file.key);
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        minWidth: 0,
                      }}
                    >
                      <Box sx={{ flex: "0 0 auto", display: "flex" }}>
                        <FileIcon file={file} puburl={puburl} />
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {extractFilename(file.key)}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{new Date(file.uploaded).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    {isDirectory(file) ? "" : humanReadableSize(file.size)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Grid container sx={{ paddingBottom: "48px" }}>
      {files.map((file) => (
        <Grid item key={file.key} xs={12} sm={6} md={4} lg={3} xl={2}>
          <ListItemButton
            selected={multiSelected?.includes(file.key)}
            onClick={() => {
              if (multiSelected !== null) {
                onMultiSelect(file.key);
              } else if (isDirectory(file)) {
                openFile(file);
              } else openFile(file);
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              onMultiSelect(file.key);
            }}
            sx={{ userSelect: "none" }}
          >
            <ListItemIcon>
              <FileIcon file={file} puburl={puburl} />
            </ListItemIcon>
            <ListItemText
              primary={extractFilename(file.key)}
              primaryTypographyProps={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              secondary={
                <React.Fragment>
                  <Box
                    sx={{
                      display: "inline-block",
                      minWidth: "160px",
                      marginRight: 1,
                    }}
                  >
                    {new Date(file.uploaded).toLocaleString()}
                  </Box>
                  {!isDirectory(file) && humanReadableSize(file.size)}
                </React.Fragment>
              }
            />
          </ListItemButton>
        </Grid>
      ))}
    </Grid>
  );
}

export default FileGrid;
